import {
  CRITICAL_WORKFLOW_PAIN_WINDOW_INSTRUMENTATION_VERSION,
  CRITICAL_WORKFLOW_PAIN_WINDOW_VITAL_NAME,
  MAX_PAIN_OBSERVATION_DURATION_MS,
} from './constants';
import type {
  LoafObservationRuntime,
  LoafPerformanceMetrics,
} from './loafObservationRuntime';
import {
  getInteractionMetrics,
  isEventTimingEntry,
  type RecordedEventTiming,
} from './performanceMetrics';
import type {
  CriticalWorkflowDatadogContext,
  CriticalWorkflowDurationVitalOptions,
  CriticalWorkflowInitiatingEvent,
  CriticalWorkflowObservationOptions,
  RecordCriticalWorkflowDurationVital,
} from './types';
import {waitForPainWindowDelivery} from './waitForPainWindowDelivery';
import {createWorkflowSamplingPolicy} from './workflowSamplingPolicy';

const EVENT_TIMING_DURATION_THRESHOLD_MS = 16;
const MAX_RECENT_EVENT_TIMINGS = 200;
const EVENT_TIMING_OBSERVER_OPTIONS: PerformanceObserverInit & {
  durationThreshold: number;
} = {
  type: 'event',
  buffered: true,
  durationThreshold: EVENT_TIMING_DURATION_THRESHOLD_MS,
};

interface WindowedObservation {
  workflow: string;
  windowDurationMs: number;
  minStartIntervalMs: number;
  startTime: number;
  endTime: number;
  initiatingEvent?: CriticalWorkflowInitiatingEvent;
  workflowContext?: CriticalWorkflowObservationOptions['workflowContext'];
  recordAbandoned: boolean;
  retriggerCount: number;
  collectorId: number;
  timeoutId: number;
  backgroundedAt?: number;
  cancelDelivery?: () => void;
}

export class WindowedPainObserver {
  private eventTimingObserver: PerformanceObserver | undefined;
  private eventTimingSubscriberCount = 0;
  private readonly recentEventTimings: RecordedEventTiming[] = [];
  private readonly observations = new Map<string, WindowedObservation>();

  constructor(
    private readonly runtime: LoafObservationRuntime,
    private readonly recordDurationVital: RecordCriticalWorkflowDurationVital,
    private readonly samplingPolicy = createWorkflowSamplingPolicy()
  ) {}

  // Keep browser event timings available to attach input latency to workflow reports.
  // This observer runs independently of the pain-window and input-lag enable flags.
  initialize(): () => void {
    if (
      !this.runtime.isSupported() ||
      !this.runtime.supportsEntryType('event')
    ) {
      return () => {};
    }

    this.eventTimingSubscriberCount += 1;
    if (this.eventTimingObserver == null) {
      this.eventTimingObserver = new PerformanceObserver(list => {
        this.recordEventTimings(list.getEntries());
      });
      this.eventTimingObserver.observe(EVENT_TIMING_OBSERVER_OPTIONS);
    }

    let stopped = false;
    return () => {
      if (stopped) {
        return;
      }
      stopped = true;
      this.eventTimingSubscriberCount -= 1;
      if (this.eventTimingSubscriberCount === 0) {
        this.eventTimingObserver?.disconnect();
        this.eventTimingObserver = undefined;
        this.recentEventTimings.length = 0;
      }
    };
  }

  /**
   * Starts a timed measurement. Repeated triggers of the same workflow share the
   * current window, keeping its original end time and the first trigger's context.
   */
  observe(options: CriticalWorkflowObservationOptions): boolean {
    const windowDurationMs = this.samplingPolicy.windowDurationMs(
      options.workflow,
      options.windowDurationMs
    );
    if (
      typeof document === 'undefined' ||
      document.visibilityState !== 'visible' ||
      !this.samplingPolicy.isEnabled(options.workflow) ||
      !this.isValid(options.workflow, windowDurationMs) ||
      !this.runtime.isSupported()
    ) {
      return false;
    }

    const now = performance.now();
    // A repeated trigger joins the current window without starting another one,
    // so the cooldown check only applies after this branch.
    const existing = this.observations.get(options.workflow);
    if (existing != null) {
      if (now < existing.endTime) {
        existing.retriggerCount += 1;
        return true;
      }
      this.scheduleCompletion(options.workflow);
      return false;
    }

    if (!this.samplingPolicy.canStart(options.workflow, now)) {
      return false;
    }
    const started = this.runtime.createCollector(windowDurationMs);
    if (started == null) {
      return false;
    }
    this.samplingPolicy.recordStart(options.workflow, started.startTime);
    const observation: WindowedObservation = {
      workflow: options.workflow,
      windowDurationMs,
      minStartIntervalMs: this.samplingPolicy.minIntervalMs(options.workflow),
      startTime: started.startTime,
      endTime: started.startTime + windowDurationMs,
      initiatingEvent: options.initiatingEvent,
      workflowContext:
        options.workflowContext == null
          ? undefined
          : {...options.workflowContext},
      recordAbandoned: options.recordAbandoned !== false,
      retriggerCount: 0,
      collectorId: started.collectorId,
      timeoutId: 0,
    };
    observation.timeoutId = window.setTimeout(
      () => this.scheduleCompletion(options.workflow),
      windowDurationMs
    );
    this.observations.set(options.workflow, observation);
    return true;
  }

  markBackgrounded(): void {
    for (const observation of this.observations.values()) {
      observation.backgroundedAt ??= performance.now();
    }
  }

  handlePageAbandonment(): void {
    this.markBackgrounded();
    if (this.observations.size === 0) {
      return;
    }

    const endTime = performance.now();
    for (const observation of this.observations.values()) {
      this.runtime.truncateCollectorEnd(observation.collectorId, endTime);
    }
    this.runtime.drain();
    this.drainPendingEventTimingEntries();

    for (const observation of this.observations.values()) {
      const duration = Math.min(
        observation.windowDurationMs,
        Math.max(0, endTime - observation.startTime)
      );
      if (!observation.recordAbandoned) {
        this.discardObservation(observation);
        continue;
      }
      const result = this.runtime.finalizeCollector(
        observation.collectorId,
        duration
      );
      if (result == null) {
        this.discardObservation(observation);
        continue;
      }
      const {
        duration: observedDuration,
        measurementComplete: _complete,
        ...metrics
      } = result;
      this.finishObservation(observation, {
        startTime: performance.timeOrigin + observation.startTime,
        duration: observedDuration,
        context: this.getContext(
          observation,
          metrics,
          'abandoned',
          false,
          observedDuration
        ),
      });
    }
  }

  private isValid(workflow: string, duration: number): boolean {
    return (
      workflow.length > 0 &&
      Number.isFinite(duration) &&
      duration > 0 &&
      duration <= MAX_PAIN_OBSERVATION_DURATION_MS
    );
  }

  private scheduleCompletion(workflow: string): void {
    const observation = this.observations.get(workflow);
    if (observation == null || observation.cancelDelivery != null) {
      return;
    }
    observation.cancelDelivery = waitForPainWindowDelivery(deliveryComplete =>
      this.completeObservation(workflow, deliveryComplete)
    );
  }

  private completeObservation(
    workflow: string,
    deliveryComplete: boolean
  ): void {
    const observation = this.observations.get(workflow);
    if (observation == null) {
      return;
    }
    this.drainPendingEventTimingEntries();
    const result = this.runtime.finalizeCollector(
      observation.collectorId,
      observation.windowDurationMs
    );
    if (result == null) {
      this.discardObservation(observation);
      return;
    }
    const {duration, measurementComplete, ...metrics} = result;
    this.finishObservation(observation, {
      startTime: performance.timeOrigin + observation.startTime,
      duration,
      context: this.getContext(
        observation,
        metrics,
        'completed',
        deliveryComplete && measurementComplete,
        duration
      ),
    });
  }

  private getContext(
    observation: WindowedObservation,
    metrics: LoafPerformanceMetrics,
    outcome: 'completed' | 'abandoned',
    measurementComplete: boolean,
    duration: number
  ): CriticalWorkflowDatadogContext {
    return {
      instrumentationVersion:
        CRITICAL_WORKFLOW_PAIN_WINDOW_INSTRUMENTATION_VERSION,
      workflow: observation.workflow,
      backgrounded:
        observation.backgroundedAt != null &&
        observation.backgroundedAt <= observation.startTime + duration,
      measurementComplete,
      outcome,
      configuredWindowDurationMs: observation.windowDurationMs,
      retriggerCount: observation.retriggerCount,
      minStartIntervalMs: observation.minStartIntervalMs,
      ...(observation.workflowContext == null
        ? {}
        : {workflowContext: observation.workflowContext}),
      ...metrics,
      ...getInteractionMetrics(
        this.recentEventTimings,
        observation.initiatingEvent
      ),
    };
  }

  private finishObservation(
    observation: WindowedObservation,
    vital: CriticalWorkflowDurationVitalOptions
  ): void {
    window.clearTimeout(observation.timeoutId);
    observation.cancelDelivery?.();
    this.observations.delete(observation.workflow);
    this.recordDurationVital(CRITICAL_WORKFLOW_PAIN_WINDOW_VITAL_NAME, vital);
  }

  private discardObservation(observation: WindowedObservation): void {
    window.clearTimeout(observation.timeoutId);
    observation.cancelDelivery?.();
    this.observations.delete(observation.workflow);
    this.runtime.cancelCollector(observation.collectorId);
  }

  private recordEventTimings(entries: PerformanceEntryList): void {
    for (const entry of entries) {
      if (!isEventTimingEntry(entry)) {
        continue;
      }
      this.recentEventTimings.push({
        name: entry.name,
        startTime: entry.startTime,
        duration: entry.duration,
        processingStart: entry.processingStart,
        processingEnd: entry.processingEnd,
        interactionId: entry.interactionId,
      });
    }
    if (this.recentEventTimings.length > MAX_RECENT_EVENT_TIMINGS) {
      this.recentEventTimings.splice(
        0,
        this.recentEventTimings.length - MAX_RECENT_EVENT_TIMINGS
      );
    }
  }

  private drainPendingEventTimingEntries(): void {
    if (this.eventTimingObserver != null) {
      this.recordEventTimings(this.eventTimingObserver.takeRecords());
    }
  }
}
